import $ from 'jquery';
import { defaultsDeep } from 'lodash';
import Radio from 'backbone.radio';
import videojs from 'video.js';
import 'videojs-markers';
import qs from '@silvermine/videojs-quality-selector';
import { createApp } from 'vue'
import VideoPlayer from "../shared/components/video_player.vue"

const DEFAULTS = {
  fluid: true,
  aspectRatio: '16:9',
  controlBar: {
    children: [
      "playToggle",
      "volumePanel",
      "currentTimeDisplay",
      "durationDisplay",
      "progressControl",
      "playbackRateMenuButton",
      "subsCapsButton",
      "fullscreenToggle",
    ],
    volumePanel: { inline: false }
  },
  playbackRates: [0.5, 1, 1.5, 2],
  html5: {
    vhs: {
      // XXX: Looks like experimental ABR better meets my expectations.
      experimentalBufferBasedABR: true,
      // XXX: Force start playing with high quality by disabling limitRenditionByPlayerDimensions and setting high initial bandwidth.
      bandwidth: 100000000,
      limitRenditionByPlayerDimensions: false
    }
  }
};
const DATA_PROPERTY = 'videoPlayer'; // data property to store player object

const globalChannel = Radio.channel('global');

qs(videojs); // QualitySelector

export function initVideoPlayer(element, options) {
  options = defaultsDeep(options || {}, DEFAULTS);

  const $videoEl = $(element);
  if ($videoEl.data(DATA_PROPERTY)) return; // Already initialized

  // Checking if there is no HLS type video in video player source
  if ($videoEl.find("[type='application/x-mpegURL']").length === 0) {
    options.controlBar.children.push('QualitySelector')
  }

  // VideoJS Player initialization
  const videoId = $(element).prop('id');
  let player = videojs.getPlayer(videoId);
  const elementInDOM = document.getElementById(videoId);

  // If there's a valid player and it's not disposed and the element matches
  if (player && !player.isDisposed() && elementInDOM && player.el() === elementInDOM) {
    return player; // Already initialized and DOM matches
  }

  // If the player exists but is disposed (or doesn't match the DOM), delete it
  if (player && player.isDisposed()) {
    delete videojs.players[videoId]; // Clean up internal cache
  }

  // Also: if somehow the old player is attached to a different DOM node (e.g. via tab remount)
  if (player && player.el() !== elementInDOM) {
    player.dispose();
    delete videojs.players[videoId];
  }

  // Clean up any jQuery data (your own tracking)
  $(element).removeData(DATA_PROPERTY);

  // Finally initialize safely
  player = videojs(videoId, options);

  const eventLabel = player.tagAttributes['data-event-label'];
  const sendImpression = player.tagAttributes['data-send-impression'];
  let videoPlayedFlag = false;
  let videoFinishedFlag = false;
  let playerinitdone = false;

  player.on('play', () => {
    if (!videoPlayedFlag) {
      trackEvent('Play', eventLabel);
      videoPlayedFlag = true;
    }
  });
  player.on('ended', () => {
    if (!videoFinishedFlag) {
      trackEvent('Completed', eventLabel);
      videoFinishedFlag = true;
    }
  });

  waitForMarkers(player, () => {
    player.markers({ // Initializing Chapter Markers Plugin
      markerTip: {
        display: true,
        text: (marker) => marker.text
      },
    });
  });

  const seekCallback = seek => {
    player.currentTime(seek)
  }
  const radioEvent = `video_player:${videoId}:seek`

  // Navigation from markers
  globalChannel.on(radioEvent, seekCallback);

  player.on('dispose', () => {
    globalChannel.off(radioEvent, seekCallback)
  })

  player.on('canplaythrough', () => { // we use 'canplaythrough' instead of 'loadmetadata' because of iOs bugs
    if(!playerinitdone) {
      // Making list of Chapter Markers
      const markers = []

      player.textTracks().tracks_.forEach(track => {
        if (track.kind == 'chapters') {
          const chapters = track.cues_;
          if (chapters) {
            chapters.forEach(chapter => {
              markers.push({
                time: chapter.startTime,
                text: chapter.text
              })
            })
          }
        }
      })
      player.markers.add(markers);
      playerinitdone = true;
    }
  });

  setup50PercentViewedTracker(player, eventLabel);

  // Send impression event at the next cycle because GA and snowplow can be not initialized yet.
  if (sendImpression) setTimeout(() => { trackEvent('Impression', eventLabel) }, 0);

  const back10button = player.controlBar.addChild('button');
  const back10buttonDOM = back10button.el();

  back10button.addClass('vj-back10');
  $(back10buttonDOM).html("&#8617; <span>10s</span>");

  $(`#${videoId}-player`).on('touchstart click', '.vj-back10', () => {
    player.currentTime(player.currentTime() - 10);
  });


  $videoEl.data(DATA_PROPERTY, player); // associate player object with video element

  return player;
}

export function destroyVideoPlayer(videoElement) {
  if (!videoElement) return;

  const $el = $(videoElement);
  const videoId = $el.prop('id');

  // Get the player using videojs API
  const player = videojs.getPlayer(videoId);

  if (player && !player.isDisposed()) {
    player.dispose();
  }

  // Cleanup: remove data attribute and DOM references
  $el.removeData(DATA_PROPERTY);

  // Optional: remove global player reference
  if (videojs.players && videojs.players[videoId]) {
    delete videojs.players[videoId];
  }
}

export function createAppVideoPlayer(parent, changeId) {
  const $videoWrappers = $('.insert-video-player', parent)

  $videoWrappers.each((_i, el) => {
    const $wrapper = $(el)

    // Prevent double-mounting
    if ($wrapper.data('vueApp')) return

    const videoData = $wrapper.data('video')

    if (changeId) {
      videoData.videoUniqueId = true
    }

    // Create and mount Vue app
    const app = createApp(VideoPlayer, { videoProp: videoData })
    app.mount(el)

    // Remember the Vue app instance for later cleanup
    $wrapper.data('vueApp', app)
  })
}

export function destroyAppVideoPlayer(parent) {
  $('.insert-video-player', parent).each((_i, el) => {
    const $wrapper = $(el)
    const app = $wrapper.data('vueApp')

    if (app) {
      app.unmount()
      $wrapper.removeData('vueApp')
    }

    // Also clean up native player instances
    $wrapper.find('video').each((_index, videoEl) => {
      destroyVideoPlayer(videoEl)
    })
  })
}

function waitForMarkers(player, callback, retry = 10) {
  if (typeof player.markers === 'function') {
    callback();
  } else if (retry > 0) {
    setTimeout(() => waitForMarkers(player, callback, retry - 1), 100); // retry every 100ms
  }
}

// Setup custom GA event tracking when 50% of video watched
function setup50PercentViewedTracker(player, eventLabel) {
  let durationWatcherInterval,
      notifiedGA = false;

  const percentWatcher = function() {
    if (notifiedGA) return;

    const duration = player.duration();
    if (!duration) return;

    const viewedPercentage = Math.round(
      player.currentTime() / duration * 100
    );

    if (viewedPercentage >= 50) {
      notifiedGA = true;
      trackEvent('Watched-50', eventLabel);
      stopWatch();
    }
  };

  const stopWatch = function() {
    if (durationWatcherInterval) {
      clearInterval(durationWatcherInterval);
      durationWatcherInterval = null;
    }
  };

  const startWatch = function() {
    if (notifiedGA || durationWatcherInterval) return;
    durationWatcherInterval = setInterval(percentWatcher, 1000);
  }

  player.on('play', startWatch);
  player.on('dispose', stopWatch);
  player.on('pause', stopWatch);
  player.on('seek', stopWatch);
}

function trackEvent(event, eventLabel) {
  if (!eventLabel) return
  if ($('body.relaunch').length) return // skip for admin pages

  ga('send', 'event', 'VJS Player Video', event, eventLabel);

  snowplow('trackStructEvent', {
    category: 'VJS Player Video',
    action: event,
    label: eventLabel
  });
}
